"use client";

import { useUpdateRef } from "@/hooks/useUpdateRef";
import { useCallback, useEffect, useRef, useState } from "react";

const elements = new Map<string, HTMLAudioElement | HTMLVideoElement>();

export const useGlobalMediaPlayer = (
  eleRef?: React.RefObject<HTMLAudioElement | HTMLVideoElement | null>
) => {
  const [id] = useState(crypto.randomUUID());
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const bridgeRef = useUpdateRef({
    id,
    isPlaying,
  });

  const handlePlay = useCallback(() => {
    for (const [aid, video] of elements) {
      if (aid !== bridgeRef.current?.id) {
        video.pause();
      }
    }

    setIsPlaying(true);
  }, [bridgeRef]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const play = useCallback(() => {
    audioRef.current?.play();
    videoRef.current?.play();
    eleRef?.current?.play();
  }, [eleRef]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    videoRef.current?.pause();
    eleRef?.current?.pause();
  }, [eleRef]);

  const toggle = useCallback(() => {
    if (bridgeRef.current?.isPlaying) {
      pause();
    } else {
      play();
    }
  }, [bridgeRef, pause, play]);

  const renderGlobalAudio = (
    props: React.DetailedHTMLProps<
      React.AudioHTMLAttributes<HTMLAudioElement>,
      HTMLAudioElement
    >
  ) => {
    return (
      <audio
        {...props}
        ref={audioRef}
        onPlay={(e) => {
          handlePlay();
          props?.onPlay?.(e);
        }}
        onPause={(e) => {
          handlePause();
          props?.onPause?.(e);
        }}
      />
    );
  };

  const renderGlobalVideo = (
    props: React.DetailedHTMLProps<
      React.AudioHTMLAttributes<HTMLVideoElement>,
      HTMLVideoElement
    >
  ) => {
    return (
      <video
        {...props}
        ref={videoRef}
        onPlay={(e) => {
          handlePlay();
          props?.onPlay?.(e);
        }}
        onPause={(e) => {
          handlePause();
          props?.onPause?.(e);
        }}
      />
    );
  };

  useEffect(() => {
    if (audioRef.current) {
      elements.set(id, audioRef.current);
    }

    if (videoRef.current) {
      elements.set(id, videoRef.current);
    }

    if (eleRef?.current) {
      elements.set(id, eleRef.current);
    }

    const currentAudio = audioRef.current;
    const currentVideo = videoRef.current;
    return () => {
      if (currentAudio || currentVideo) {
        elements.delete(id);
      }
    };
  }, [eleRef, id]);

  useEffect(() => {
    if (eleRef?.current) {
      eleRef.current.id = id;
    }
  }, [eleRef, id]);

  return {
    renderGlobalAudio,
    renderGlobalVideo,
    isPlaying,
    audioRef,
    play,
    pause,
    toggle,
    handlePlay,
    handlePause,
  };
};
